/**
 * Markdown parsing utilities
 * Functions for parsing markdown files, extracting front matter, and converting to structured formats
 */

/**
 * Parse markdown file content and extract front matter
 * @param {string} markdownContent - The markdown content to parse
 * @returns {Object} Object containing frontMatter, body, and fullContent
 */
export const parseMarkdownFile = (markdownContent) => {
    // Parse front matter (YAML between --- markers)
    const frontMatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
    const match = markdownContent.match(frontMatterRegex);
    
    let frontMatter = {};
    let body = markdownContent;
    
    if (match) {
        // Parse front matter
        const frontMatterText = match[1];
        body = match[2];
        
        // Simple YAML parser for key: value pairs
        frontMatterText.split('\n').forEach(line => {
            const trimmedLine = line.trim();
            if (trimmedLine && !trimmedLine.startsWith('#')) {
                const colonIndex = trimmedLine.indexOf(':');
                if (colonIndex > 0) {
                    const key = trimmedLine.substring(0, colonIndex).trim();
                    let value = trimmedLine.substring(colonIndex + 1).trim();
                    
                    // Remove quotes if present
                    if ((value.startsWith('"') && value.endsWith('"')) ||
                        (value.startsWith("'") && value.endsWith("'"))) {
                        value = value.slice(1, -1);
                    }
                    
                    frontMatter[key] = value;
                }
            }
        });
    }
    
    return {
        frontMatter,
        body: body.trim(),
        fullContent: markdownContent
    };
};

/**
 * Parse markdown content and convert to detailSection format (object with section keys)
 * Splits markdown by level 2 headings (##) into sections
 * @param {string} markdownContent - The markdown content to parse
 * @returns {Object} Object with section names as keys and content as values
 */
export const parseMarkdownToDetailSection = (markdownContent) => {
    const parsed = parseMarkdownFile(markdownContent);
    const body = parsed.body;
    
    // Split by ## headings (level 2 headings)
    const sections = {};
    const lines = body.split('\n');
    let currentSection = null;
    let currentContent = [];
    
    // Handle content before first heading
    let preHeadingContent = [];
    let foundFirstHeading = false;
    
    lines.forEach(line => {
        // Check if line is a level 2 heading (##)
        if (line.trim().startsWith('## ')) {
            // Save previous section if exists
            if (currentSection && currentContent.length > 0) {
                sections[currentSection] = currentContent.join('\n').trim();
            }
            
            // Start new section
            const headingText = line.trim().substring(3).trim();
            // Clean up heading - remove links, keep text
            const cleanHeading = headingText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
            currentSection = cleanHeading;
            currentContent = [];
            foundFirstHeading = true;
        } else if (line.trim().startsWith('### ')) {
            // Level 3 heading - include in current section
            currentContent.push(line);
        } else {
            if (!foundFirstHeading) {
                preHeadingContent.push(line);
            } else {
                currentContent.push(line);
            }
        }
    });
    
    // Save last section
    if (currentSection && currentContent.length > 0) {
        sections[currentSection] = currentContent.join('\n').trim();
    }
    
    // If there's content before first heading, add it to a "Cover" or "Introduction" section
    if (preHeadingContent.length > 0) {
        const preContent = preHeadingContent.join('\n').trim();
        if (preContent) {
            // Check if "Cover" section already exists, if not create it
            if (!sections['Cover'] && !sections['Introduction']) {
                sections['Cover'] = preContent;
            } else if (sections['Cover']) {
                sections['Cover'] = preContent + '\n\n' + sections['Cover'];
            }
        }
    }
    
    return sections;
};

/**
 * Normalize a markdown heading or UI label for section matching.
 * @param {string} title
 */
export const normalizeMarkdownSectionTitle = (title) =>
    String(title || '')
        .replace(/^\d+(?:\.\d+)*\s+/, '')
        .replace(/&/g, ' and ')
        .replace(/[^\w\s-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

/**
 * Split markdown body by heading level (1 = #, 2 = ##, …).
 * @param {string} markdownContent
 * @param {number} level
 * @returns {Record<string, string>}
 */
export const parseMarkdownSectionsByLevel = (markdownContent, level = 2) => {
    const parsed = parseMarkdownFile(markdownContent);
    const prefix = `${'#'.repeat(level)} `;
    const deeper = `${'#'.repeat(level + 1)} `;
    const sections = {};
    const lines = parsed.body.split('\n');
    let currentSection = null;
    let currentContent = [];

    lines.forEach((line) => {
        const trimmed = line.trim();
        if (trimmed.startsWith(prefix) && !trimmed.startsWith(deeper)) {
            if (currentSection !== null && currentContent.length > 0) {
                sections[currentSection] = currentContent.join('\n').trim();
            }
            const headingText = trimmed.slice(prefix.length).trim();
            const cleanHeading = headingText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
            currentSection = cleanHeading;
            currentContent = [];
        } else if (currentSection !== null) {
            currentContent.push(line);
        }
    });

    if (currentSection !== null && currentContent.length > 0) {
        sections[currentSection] = currentContent.join('\n').trim();
    }

    return sections;
};

/**
 * Extract one section from markdown by UI label (matches H2 first, then H1).
 * @param {string} markdownContent
 * @param {string} sectionTitle
 * @returns {string}
 */
export const extractMarkdownSection = (markdownContent, sectionTitle) => {
    if (!markdownContent) return '';
    if (!sectionTitle) return markdownContent;

    const target = normalizeMarkdownSectionTitle(sectionTitle);
    if (!target) return markdownContent;

    const findSection = (sections, headingPrefix) => {
        for (const [heading, body] of Object.entries(sections)) {
            if (normalizeMarkdownSectionTitle(heading) === target) {
                return `${headingPrefix}${heading}\n\n${body}`.trim();
            }
        }
        return '';
    };

    const h2Match = findSection(parseMarkdownSectionsByLevel(markdownContent, 2), '## ');
    if (h2Match) return h2Match;

    const h1Match = findSection(parseMarkdownSectionsByLevel(markdownContent, 1), '# ');
    if (h1Match) return h1Match;

    return '';
};

